import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { clienteNome, Modal, ConfirmDialog, LoadingSpinner } from '../components/shared';

function BancaForm({ clientiList, onSave, onClose, loading }) {
  const [d, setD] = useState({ id_cliente: '', nome_banca: '', iban: '', filiale: '', note: '' });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(d); }}>
      <div className="form-grid">
        <div className="form-group form-full">
          <label className="form-label">Cliente</label>
          <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
            <option value="">— Seleziona cliente —</option>
            {clientiList.map(c => <option key={c.id} value={c.id}>{clienteNome(c)} ({c.codice})</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Nome Banca *</label>
          <input className="form-control" value={d.nome_banca} onChange={e => set('nome_banca', e.target.value)} required placeholder="Es. Intesa Sanpaolo" />
        </div>
        <div className="form-group">
          <label className="form-label">Filiale</label>
          <input className="form-control" value={d.filiale} onChange={e => set('filiale', e.target.value)} placeholder="Es. Roma Centro" />
        </div>
        <div className="form-group form-full">
          <label className="form-label">IBAN</label>
          <input className="form-control" value={d.iban} onChange={e => set('iban', e.target.value.toUpperCase())} maxLength={27} placeholder="IT60X0000000000000000000000" style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note} onChange={e => set('note', e.target.value)} rows={2} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: '1px solid var(--border)', marginTop: 20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvataggio…' : 'Salva Conto'}</button>
      </div>
    </form>
  );
}

export default function Banche({ addToast }) {
  const [rows, setRows]       = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [delItem, setDelItem]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [q, setQ]               = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([api.getBanche(), api.getClienti({ limit: 200 })])
      .then(([b, cl]) => { setRows(b); setClienti(cl.rows); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (formData) => {
    setSaving(true);
    try { await api.createBanca(formData); addToast('Conto bancario aggiunto', 'success'); setShowForm(false); load(); }
    catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteBanca(delItem.id); addToast('Eliminato', 'success'); setDelItem(null); load(); }
    catch (e) { addToast(e.message, 'error'); }
  };

  // Group by client
  const filtered = rows.filter(r => {
    if (!q) return true;
    const name = clienteNome(r).toLowerCase();
    return name.includes(q.toLowerCase()) || (r.nome_banca || '').toLowerCase().includes(q.toLowerCase()) || (r.iban || '').toLowerCase().includes(q.toLowerCase());
  });

  // Group rows by cliente
  const grouped = filtered.reduce((acc, r) => {
    const key = r.id_cliente || 0;
    if (!acc[key]) acc[key] = { cliente: r, conti: [] };
    acc[key].conti.push(r);
    return acc;
  }, {});

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">Controllo Banche</div>
          <div className="page-subtitle">{rows.length} conti bancari registrati</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Aggiungi Conto</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
        {[['Conti totali', rows.length, 'var(--accent)'], ['Clienti con conto', Object.keys(grouped).length, 'var(--c-blue)'], ['Banche diverse', [...new Set(rows.map(r => r.nome_banca))].length, 'var(--c-green)']].map(([l, v, c]) => (
          <div key={l} className="kpi-card">
            <div className="kpi-accent" style={{ background: c }} />
            <div className="kpi-label">{l}</div>
            <div className="kpi-value">{v}</div>
          </div>
        ))}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca cliente, banca, IBAN…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Object.values(grouped).map(({ cliente, conti }) => (
            <div key={cliente.id_cliente} className="card">
              <div className="card-header">
                <div>
                  <span className="card-title">{clienteNome(cliente)}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-faint)', marginLeft: 10 }}>{conti.length} {conti.length === 1 ? 'conto' : 'conti'}</span>
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Banca</th><th>IBAN</th><th>Filiale</th><th>Note</th><th></th></tr></thead>
                  <tbody>
                    {conti.map(b => (
                      <tr key={b.id}>
                        <td className="fw-500">{b.nome_banca || '—'}</td>
                        <td className="td-mono" style={{ letterSpacing: '0.5px' }}>{b.iban || '—'}</td>
                        <td>{b.filiale || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{b.note || '—'}</td>
                        <td><button className="btn btn-sm btn-danger" onClick={() => setDelItem(b)}>×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {Object.keys(grouped).length === 0 && (
            <div className="card"><div className="empty-state"><div className="empty-state-icon">💳</div><div className="empty-state-title">Nessun conto bancario registrato</div></div></div>
          )}
        </div>
      )}

      {showForm && (
        <Modal title="Aggiungi Conto Bancario" onClose={() => setShowForm(false)}>
          <BancaForm clientiList={clienti} onSave={handleSave} onClose={() => setShowForm(false)} loading={saving} />
        </Modal>
      )}
      {delItem && <ConfirmDialog message={`Eliminare il conto IBAN ${delItem.iban || delItem.nome_banca}?`} onConfirm={handleDelete} onCancel={() => setDelItem(null)} />}
    </div>
  );
}
